import React, { useEffect, useMemo, useRef, useState, } from 'react'
import styled, { keyframes, } from 'styled-components'
import ReactPixel from 'react-facebook-pixel'

import Section3 from './Section3'
import Section4 from './Section4'

const CONTACT_EMAIL = 'contact.us@d405.io'

const heroReveal = keyframes`
  from {
    opacity: 0;
    transform: translateY(18px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const serviceItems = [
  {
    step: '01',
    title: 'MVP / PoC 개발',
    problem: '기획서가 완성되지 않았거나 시장 검증이 먼저 필요한 단계',
    output: '핵심 기능 정의, 화면 흐름, 검증용 첫 버전',
  },
  {
    step: '02',
    title: '웹 / 앱 서비스 개발',
    problem: '사용자 서비스와 운영 관리 기능을 한 번에 구축해야 하는 단계',
    output: '사용자 화면, 관리자, API, 배포 환경',
  },
  {
    step: '03',
    title: '업무 시스템 구축',
    problem: '반복 업무와 수기 관리가 늘어나 내부 운영이 느려지는 단계',
    output: '관리 도구, 권한 구조, 자동화 플로우',
  },
  {
    step: '04',
    title: '운영 서비스 개선',
    problem: '출시 이후 기능 추가, 유지보수, 구조 개선이 필요한 단계',
    output: '기능 개선, 성능 점검, 유지보수 체계',
  },
]

const processItems = [
  {
    title: '상담',
    description: '만들고 싶은 서비스, 현재 단계, 예산, 일정, 우려 지점을 먼저 확인합니다.',
  },
  {
    title: '범위 정의',
    description: '핵심 기능, 제외할 기능, 필요한 화면과 관리자 범위를 정리합니다.',
  },
  {
    title: '설계와 디자인',
    description: '사용 흐름, 와이어프레임, UI 방향을 정하고 개발 가능한 산출물로 구체화합니다.',
  },
  {
    title: '개발과 QA',
    description: '프론트엔드, 백엔드, 인프라를 구현하고 테스트와 수정 과정을 반복합니다.',
  },
  {
    title: '출시와 운영',
    description: '배포, 모니터링, 유지보수, 후속 개선 범위를 이어서 관리합니다.',
  },
]

const pricingItems = [
  {
    title: 'MVP 제작',
    price: '200만원부터',
    meta: '1주-4주',
    description: '핵심 기능 중심의 검증용 웹/앱 서비스',
  },
  {
    title: '신규 서비스 구축',
    price: '1,500만원부터',
    meta: '4주 이상',
    description: '사용자 서비스, 관리자, API, 배포 환경 포함',
  },
  {
    title: '개선 / 유지보수',
    price: '상담 후 결정',
    meta: '2주 이상 또는 월 단위',
    description: '운영 중인 서비스의 기능 추가와 구조 개선',
  },
]

const faqItems = [
  {
    question: '기획서가 없어도 상담할 수 있나요?',
    answer: '가능합니다. 아이디어, 참고 서비스, 꼭 필요한 기능만 있어도 상담 과정에서 구현 범위를 함께 정리합니다.',
  },
  {
    question: '디자인만 있고 개발이 필요한 경우도 가능한가요?',
    answer: '가능합니다. 디자인 산출물의 개발 가능성을 확인하고 API, 관리자, 배포 범위를 같이 정의합니다.',
  },
  {
    question: '출시 후 유지보수도 맡길 수 있나요?',
    answer: '가능합니다. 장애 대응, 기능 개선, 서버 운영, 운영 데이터 확인까지 프로젝트 상황에 맞춰 제안합니다.',
  },
  {
    question: '상담 전에 무엇을 준비하면 좋나요?',
    answer: '만들고 싶은 서비스 설명, 참고 링크, 희망 일정, 예산 범위가 있으면 더 빠르게 현실적인 방향을 잡을 수 있습니다.',
  },
]

const LandingPage = styled.div`
  min-height: 100vh;
  color: #17202a;
  background: #f7f9fb;
  font-family: 'NanumSquareRound', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  * {
    box-sizing: border-box;
  }
`

interface HeaderProps {
  solid: boolean
}

const Header = styled.header<HeaderProps>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 20;
  background: ${ props => props.solid ? 'rgba(11, 18, 32, 0.92)' : 'rgba(11, 18, 32, 0)' };
  backdrop-filter: ${ props => props.solid ? 'blur(12px)' : 'none' };
  border-bottom: 1px solid ${ props => props.solid ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0)' };
  transition: background 0.25s ease, border-color 0.25s ease, backdrop-filter 0.25s ease;
`

const HeaderInner = styled.div`
  width: min(1120px, calc(100% - 40px));
  height: 72px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;

  @media (max-width: 760px) {
    width: calc(100% - 32px);
    height: 64px;
    gap: 14px;
  }

  @media (max-width: 760px) {
    button {
      min-height: 34px;
      padding: 0 12px;
      font-size: 13px;
      box-shadow: 0 8px 18px rgba(29, 173, 143, 0.24);
    }
  }
`

const Brand = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #ffffff;
  font-size: 19px;
  font-weight: 800;
  text-decoration: none;
  white-space: nowrap;

  img {
    width: 32px;
    height: 32px;
  }

  @media (max-width: 420px) {
    font-size: 17px;
  }
`

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 22px;

  a {
    color: rgba(255, 255, 255, 0.76);
    font-size: 15px;
    font-weight: 700;
    text-decoration: none;

    &:hover {
      color: #ffffff;
    }
  }

  @media (max-width: 860px) {
    display: none;
  }
`

const Button = styled.button`
  min-height: 44px;
  padding: 0 18px;
  border: 0;
  border-radius: 8px;
  color: #08111f;
  background: #73dfc4;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 10px 24px rgba(29, 173, 143, 0.28);
  transition: background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    background: #8af0d7;
    transform: translateY(-1px);
    box-shadow: 0 14px 30px rgba(29, 173, 143, 0.32);
  }

  @media (max-width: 760px) {
    min-height: 40px;
    padding: 0 14px;
    font-size: 14px;
  }
`

const GhostButton = styled.a`
  min-height: 44px;
  padding: 0 18px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 8px;
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 800;
  text-decoration: none;
  cursor: pointer;
  transition: border-color 0.2s ease, transform 0.2s ease, background 0.2s ease;

  &:hover {
    border-color: #ffffff;
    background: rgba(255, 255, 255, 0.08);
    transform: translateY(-1px);
  }

  @media (max-width: 760px) {
    min-height: 40px;
    padding: 0 14px;
    font-size: 14px;
  }
`

const Section = styled.section`
  scroll-margin-top: 72px;
  padding: 88px 0;

  @media (max-width: 760px) {
    scroll-margin-top: 64px;
    padding: 48px 0;
  }
`

const Container = styled.div`
  width: min(1120px, calc(100% - 40px));
  margin: 0 auto;

  @media (max-width: 760px) {
    width: calc(100% - 32px);
  }
`

const Hero = styled.section`
  position: relative;
  scroll-margin-top: 0;
  min-height: clamp(720px, 100vh, 1120px);
  overflow: hidden;
  background: #111111;

  video {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0.52;
  }

  &:after {
    content: '';
    position: absolute;
    inset: 0;
    background:
      linear-gradient(90deg, rgba(8, 8, 8, 0.78) 0%, rgba(8, 8, 8, 0.52) 56%, rgba(8, 8, 8, 0.26) 100%),
      linear-gradient(0deg, rgba(8, 8, 8, 0.44) 0%, rgba(8, 8, 8, 0.12) 38%, rgba(8, 8, 8, 0.22) 100%);
  }

  @media (max-width: 760px) {
    min-height: 100vh;
    min-height: 100svh;
  }
`

const HeroInner = styled(Container)`
  position: relative;
  z-index: 1;
  min-height: clamp(720px, 100vh, 1120px);
  padding: 86px 0 64px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-height: 780px) and (min-width: 761px) {
    padding: 72px 0 52px;
  }

  @media (max-width: 760px) {
    min-height: 100vh;
    min-height: 100svh;
    padding: 88px 0 52px;
  }
`

const HeroCopy = styled.div`
  width: 100%;
  max-width: 1040px;
  text-align: center;
  animation: ${ heroReveal } 0.72s ease both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  color: #0f7f6c;
  background: rgba(115, 223, 196, 0.18);
  border: 1px solid rgba(15, 127, 108, 0.22);
  font-size: 14px;
  font-weight: 800;

  @media (max-width: 760px) {
    min-height: 28px;
    padding: 0 10px;
    font-size: 12px;
  }
`

const DarkEyebrow = styled(Eyebrow)`
  color: #a8f1de;
  background: rgba(115, 223, 196, 0.12);
  border-color: rgba(115, 223, 196, 0.3);
`

const HeroTitle = styled.h1`
  margin: 24px 0 0;
  color: #ffffff;
  font-size: 48px;
  line-height: 1.12;
  font-weight: 800;
  letter-spacing: 0;
  white-space: nowrap;

  .mobile-break {
    display: none;
  }

  @media (max-width: 760px) {
    margin-top: 18px;
    font-size: 29px;
    line-height: 1.18;
    white-space: normal;

    .desktop-space {
      display: none;
    }

    .mobile-break {
      display: block;
    }
  }

  @media (max-width: 420px) {
    font-size: 27px;
  }
`

const HeroDescription = styled.p`
  max-width: 640px;
  margin: 22px 0 0;
  color: rgba(255, 255, 255, 0.78);
  font-size: 19px;
  line-height: 1.75;
  font-weight: 400;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 760px) {
    margin-top: 16px;
    font-size: 14px;
    line-height: 1.65;
  }
`

const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-top: 28px;

  @media (max-width: 760px) {
    gap: 8px;
    margin-top: 20px;
  }
`

const HeroStats = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  width: min(780px, 100%);
  margin-left: auto;
  margin-right: auto;
  margin-top: 36px;
  text-align: left;

  @media (max-width: 620px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    margin-top: 24px;
  }
`

const Stat = styled.div`
  min-height: 86px;
  padding: 16px;
  border-radius: 8px;
  color: #ffffff;
  background: rgba(255, 255, 255, 0.09);
  border: 1px solid rgba(255, 255, 255, 0.12);
  transition: transform 0.2s ease, background 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-3px);
    background: rgba(255, 255, 255, 0.13);
    border-color: rgba(255, 255, 255, 0.22);
  }

  strong {
    display: block;
    color: #73dfc4;
    font-size: 18px;
    line-height: 1.2;
  }

  span {
    display: block;
    margin-top: 8px;
    color: rgba(255, 255, 255, 0.72);
    font-size: 13px;
    line-height: 1.4;
  }

  @media (max-width: 620px) {
    min-height: 72px;
    padding: 12px;

    strong {
      font-size: 15px;
    }

    span {
      margin-top: 6px;
      font-size: 12px;
      line-height: 1.35;
    }
  }
`

const SectionTitle = styled.div`
  max-width: 760px;
  margin-bottom: 34px;

  h2 {
    margin: 14px 0 0;
    color: #152236;
    font-size: 34px;
    line-height: 1.25;
    letter-spacing: 0;
  }

  p {
    margin: 14px 0 0;
    color: #5b697c;
    font-size: 17px;
    line-height: 1.7;
  }

  @media (max-width: 760px) {
    margin-bottom: 24px;

    h2 {
      margin-top: 10px;
      font-size: 23px;
      line-height: 1.32;
    }

    p {
      margin-top: 10px;
      font-size: 14px;
      line-height: 1.65;
    }
  }
`

const ServicesBand = styled(Section)`
  background: #ffffff;
`

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`

const ServiceCard = styled.div`
  position: relative;
  height: 100%;
  min-height: 280px;
  padding: 28px;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
  background: #f8faf9;
  border: 1px solid #e4eaf0;
  box-shadow: 0 12px 32px rgba(17, 34, 51, 0.06);
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;

  &:hover {
    transform: translateY(-5px);
    border-color: rgba(115, 223, 196, 0.7);
    box-shadow: 0 20px 44px rgba(17, 34, 51, 0.1);
  }

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: #73dfc4;
  }

  small {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    color: #0f5f55;
    background: rgba(115, 223, 196, 0.24);
    font-size: 13px;
    font-weight: 800;
  }

  h3 {
    margin: 18px 0 0;
    color: #152236;
    font-size: 25px;
    line-height: 1.35;
  }

  p {
    margin: 18px 0 0;
    color: #607086;
    font-size: 16px;
    line-height: 1.7;
    margin-bottom: 22px;
  }

  @media (max-width: 760px) {
    min-height: 0;
    padding: 22px;

    small {
      width: 32px;
      height: 32px;
      font-size: 12px;
    }

    h3 {
      margin-top: 14px;
      font-size: 21px;
    }

    p {
      margin-top: 12px;
      margin-bottom: 18px;
      font-size: 14px;
      line-height: 1.6;
    }
  }
`

const ServiceMeta = styled.div`
  display: grid;
  grid-template-columns: 80px minmax(0, 1fr);
  gap: 12px;
  margin-top: auto;
  padding-top: 18px;
  border-top: 1px solid #dde6ee;

  strong {
    color: #0f7f6c;
    font-size: 14px;
    line-height: 1.6;
  }

  span {
    color: #253247;
    font-size: 15px;
    line-height: 1.6;
    font-weight: 700;
  }

  @media (max-width: 420px) {
    grid-template-columns: 1fr;
    gap: 4px;
  }

  @media (max-width: 760px) {
    gap: 8px;
    padding-top: 14px;

    strong {
      font-size: 13px;
    }

    span {
      font-size: 14px;
      line-height: 1.5;
    }
  }
`

const ProcessBand = styled(Section)`
  background: #0f1726;
`

const LightTitle = styled(SectionTitle)`
  h2 {
    color: #ffffff;
  }

  p {
    color: rgba(255, 255, 255, 0.72);
  }
`

const ProcessList = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 1px;
  overflow: hidden;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.12);

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`

const ProcessItem = styled.div`
  height: 100%;
  min-height: 210px;
  padding: 24px;
  background: rgba(255, 255, 255, 0.06);
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.09);
    transform: translateY(-3px);
  }

  small {
    color: #73dfc4;
    font-size: 13px;
    font-weight: 800;
  }

  h3 {
    margin: 18px 0 0;
    color: #ffffff;
    font-size: 21px;
  }

  p {
    margin: 12px 0 0;
    color: rgba(255, 255, 255, 0.68);
    font-size: 15px;
    line-height: 1.7;
  }

  @media (max-width: 760px) {
    min-height: 0;
    padding: 18px;

    h3 {
      margin-top: 12px;
      font-size: 18px;
    }

    p {
      margin-top: 8px;
      font-size: 13px;
      line-height: 1.55;
    }
  }
`

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`

const PricingCard = styled.div`
  height: 100%;
  padding: 26px;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #e4eaf0;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(115, 223, 196, 0.65);
    box-shadow: 0 16px 34px rgba(17, 34, 51, 0.08);
  }

  h3 {
    margin: 0;
    color: #152236;
    font-size: 22px;
  }

  strong {
    display: inline-flex;
    margin-top: 18px;
    color: #0f7f6c;
    font-size: 25px;
    line-height: 1.2;
  }

  em {
    display: block;
    margin-top: 8px;
    color: #7a889a;
    font-size: 14px;
    line-height: 1.5;
    font-style: normal;
    font-weight: 800;
  }

  p {
    margin: 14px 0 0;
    color: #607086;
    font-size: 15px;
    line-height: 1.65;
  }

  @media (max-width: 760px) {
    padding: 20px;

    h3 {
      font-size: 19px;
    }

    strong {
      margin-top: 14px;
      font-size: 21px;
    }

    em {
      margin-top: 6px;
      font-size: 13px;
    }

    p {
      margin-top: 10px;
      font-size: 13px;
      line-height: 1.55;
    }
  }
`

const FaqGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`

const FaqCard = styled.div`
  height: 100%;
  padding: 24px;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #e4eaf0;
  transition: transform 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-3px);
    border-color: rgba(115, 223, 196, 0.65);
  }

  h3 {
    margin: 0;
    color: #152236;
    font-size: 18px;
    line-height: 1.45;
  }

  p {
    margin: 12px 0 0;
    color: #607086;
    font-size: 15px;
    line-height: 1.7;
  }

  @media (max-width: 760px) {
    padding: 20px;

    h3 {
      font-size: 16px;
      line-height: 1.4;
    }

    p {
      margin-top: 8px;
      font-size: 13px;
      line-height: 1.6;
    }
  }
`

const ContactBand = styled(Section)`
  background: #ffffff;
`

const ContactLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.88fr) minmax(320px, 1.12fr);
  gap: 48px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`

const ContactInfo = styled.div`
  p {
    color: #5b697c;
    font-size: 17px;
    line-height: 1.75;
  }

  a {
    color: #0f7f6c;
    font-weight: 800;
    text-decoration: none;
  }

  @media (max-width: 760px) {
    p {
      font-size: 14px;
      line-height: 1.65;
    }
  }
`

const Form = styled.form`
  display: grid;
  gap: 12px;
  padding: 24px;
  border-radius: 8px;
  background: #f7f9fb;
  border: 1px solid #e4eaf0;

  @media (max-width: 760px) {
    gap: 10px;
    padding: 18px;
  }
`

const Field = styled.label`
  display: grid;
  gap: 7px;
  color: #253247;
  font-size: 14px;
  font-weight: 800;

  input,
  textarea,
  select {
    width: 100%;
    border: 1px solid #d7e0ea;
    border-radius: 8px;
    background: #ffffff;
    color: #152236;
    font-size: 15px;
    line-height: 1.5;
    padding: 12px 13px;
    outline: none;
    font-family: inherit;

    &:focus {
      border-color: #54c9ad;
      box-shadow: 0 0 0 3px rgba(84, 201, 173, 0.16);
    }
  }

  textarea {
    min-height: 140px;
    resize: vertical;
  }

  @media (max-width: 760px) {
    gap: 6px;
    font-size: 13px;

    input,
    textarea,
    select {
      font-size: 14px;
      padding: 10px 11px;
    }

    textarea {
      min-height: 112px;
    }
  }
`

const Footer = styled.footer`
  padding: 34px 0 44px;
  background: #0b1220;
  color: rgba(255, 255, 255, 0.68);

  strong {
    display: block;
    color: #ffffff;
    font-size: 18px;
    margin-bottom: 12px;
  }

  p {
    margin: 6px 0 0;
    font-size: 13px;
    line-height: 1.6;
  }
`

interface RevealContainerProps {
  visible: boolean
  delay: number
}

const RevealContainer = styled.div<RevealContainerProps>`
  width: 100%;
  height: 100%;
  opacity: ${ props => props.visible ? 1 : 0 };
  transform: translateY(${ props => props.visible ? '0' : '22px' });
  transition: opacity 0.55s ease, transform 0.55s ease;
  transition-delay: ${ props => props.delay }ms;

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    transform: none;
    transition: none;
  }
`

interface RevealProps {
  children: React.ReactNode
  delay?: number
}

function Reveal(props: RevealProps) {
  const { children, delay = 0, } = props
  const [ visible, setVisible, ] = useState(false)
  const element = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const node = element.current

    if(!node) {
      return
    }

    if(!('IntersectionObserver' in window)) {
      setVisible(true)
      return
    }

    const observer = new IntersectionObserver((entries) => {
      if(entries[0].isIntersecting) {
        setVisible(true)
        observer.disconnect()
      }
    }, {
      rootMargin: '0px 0px -80px 0px',
      threshold: 0.16,
    })

    observer.observe(node)

    return () => observer.disconnect()
  }, [])

  return (
    <RevealContainer ref={ element } visible={ visible } delay={ delay }>
      { children }
    </RevealContainer>
  )
}

interface InquiryForm {
  name: string
  contact: string
  projectType: string
  budget: string
  message: string
}

function Landing() {
  const [ headerSolid, setHeaderSolid, ] = useState(false)
  const [ form, setForm, ] = useState<InquiryForm>({
    name: '',
    contact: '',
    projectType: 'MVP / PoC 개발',
    budget: '미정',
    message: '',
  })

  const inquiryMessage = useMemo(() => {
    return [
      '[프로젝트 상담 요청]',
      '',
      `이름/회사: ${ form.name || '-' }`,
      `연락처: ${ form.contact || '-' }`,
      `의뢰 유형: ${ form.projectType }`,
      `예산 범위: ${ form.budget }`,
      '',
      '프로젝트 설명',
      form.message || '-',
    ].join('\n')
  }, [ form, ])

  const fallbackMailtoHref = useMemo(() => {
    const subject = encodeURIComponent(`[D405 HOUSE] 프로젝트 상담 요청 - ${ form.name || '이름 미입력' }`)
    const body = encodeURIComponent(inquiryMessage)

    return `mailto:${ CONTACT_EMAIL }?subject=${ subject }&body=${ body }`
  }, [ form.name, inquiryMessage, ])

  useEffect(() => {
    const updateHeader = () => {
      setHeaderSolid(window.pageYOffset > 120)
    }

    updateHeader()
    window.addEventListener('scroll', updateHeader)

    return () => window.removeEventListener('scroll', updateHeader)
  }, [])

  useEffect(() => {
    if(!window.location.hash) {
      return
    }

    window.scrollTo(0, 0)

    setTimeout(() => {
      const target = document.querySelector(window.location.hash)

      if(target) {
        const top = target.getBoundingClientRect().top + window.pageYOffset - 72
        window.scrollTo(0, Math.max(top, 0))
      }
    }, 100)
  }, [])

  const openChannelTalk = () => {
    // @ts-ignore
    if(window.ChannelIO) {
      // @ts-ignore
      window.ChannelIO('openChat')
    } else {
      window.location.href = `mailto:${ CONTACT_EMAIL }`
    }

    ReactPixel.trackCustom('contact_cta_click')
  }

  const onInputChange = (key: keyof InquiryForm) => {
    return (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      setForm({
        ...form,
        [key]: event.target.value,
      })
    }
  }

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    ReactPixel.trackCustom('contact_channel_submit')

    // @ts-ignore
    if(window.ChannelIO) {
      // @ts-ignore
      window.ChannelIO('openChat', undefined, inquiryMessage)
    } else {
      window.location.href = fallbackMailtoHref
    }
  }

  return (
    <LandingPage>
      <Header solid={ headerSolid }>
        <HeaderInner>
          <Brand href='#top' aria-label='D405 HOUSE home'>
            <img src={ require('assets/images/logo.png') } alt='' />
            D405 HOUSE
          </Brand>
          <Nav aria-label='main navigation'>
            <a href='#services'>서비스</a>
            <a href='#portfolio'>포트폴리오</a>
            <a href='#process'>진행방식</a>
            <a href='#team'>팀</a>
            <a href='#pricing'>예산가이드</a>
            <a href='#faq'>FAQ</a>
            <a href='#contact'>문의</a>
          </Nav>
          <Button onClick={ openChannelTalk }>상담요청</Button>
        </HeaderInner>
      </Header>

      <Hero id='top'>
        <video
          // @ts-ignore
          playsInline={ true }
          autoPlay={ true }
          loop={ true }
          muted={ true }
          src={ require('./Section1/assets/videos/background.mp4') }
        />
        <HeroInner>
          <HeroCopy>
            <DarkEyebrow>앱/웹 서비스 외주 개발 파트너</DarkEyebrow>
            <HeroTitle>
              고객님과 함께 움직이는<span className='desktop-space'> </span><br className='mobile-break' />개발팀입니다
            </HeroTitle>
            <HeroDescription>
              기획부터 디자인, 개발, 운영 개선까지 함께 움직입니다. 아이디어를 현실적인 개발 범위로 정리하고, 예산과 일정 안에서 출시 가능한 범위를 제안합니다.
            </HeroDescription>
            <HeroStats>
              <Stat>
                <strong>MVP</strong>
                <span>
                  검증용 제품 설계와<br />
                  빠른 출시
                </span>
              </Stat>
              <Stat>
                <strong>Web/App</strong>
                <span>사용자 서비스와 관리자 구축</span>
              </Stat>
              <Stat>
                <strong>UI/UX</strong>
                <span>기획 보완과 화면 설계</span>
              </Stat>
              <Stat>
                <strong>Ops</strong>
                <span>출시 후 개선과 유지보수</span>
              </Stat>
            </HeroStats>
            <HeroActions>
              <Button onClick={ openChannelTalk }>프로젝트 상담하기</Button>
              <GhostButton href='#services'>서비스 범위 보기</GhostButton>
            </HeroActions>
          </HeroCopy>

        </HeroInner>
      </Hero>

      <ServicesBand id='services'>
        <Container>
          <Reveal>
            <SectionTitle>
              <Eyebrow>Services</Eyebrow>
              <h2>처음부터 완벽하지 않아도 괜찮습니다.</h2>
              <p>
                어디서부터 시작해야 할지부터 같이 정리하고, 지금 필요한 만큼만 먼저 만듭니다.
              </p>
            </SectionTitle>
          </Reveal>
          <ServiceGrid>
            {
              serviceItems.map((item, index) => (
                <Reveal key={ item.title } delay={ index * 80 }>
                  <ServiceCard>
                    <small>{ item.step }</small>
                    <h3>{ item.title }</h3>
                    <p>{ item.problem }</p>
                    <ServiceMeta>
                      <strong>산출물</strong>
                      <span>{ item.output }</span>
                    </ServiceMeta>
                  </ServiceCard>
                </Reveal>
              ))
            }
          </ServiceGrid>
        </Container>
      </ServicesBand>

      <Section3 id='portfolio' focused={ false } />

      <ProcessBand id='process'>
        <Container>
          <Reveal>
            <LightTitle>
              <DarkEyebrow>Process</DarkEyebrow>
              <h2>복잡한 외주 과정을 최대한 명확하게 만듭니다.</h2>
              <p>
                단계마다 무엇을 결정하고 무엇을 만들어야 하는지 분명하게 공유합니다.
              </p>
            </LightTitle>
          </Reveal>
          <ProcessList>
            {
              processItems.map((item, index) => (
                <Reveal key={ item.title } delay={ index * 70 }>
                  <ProcessItem>
                    <small>{ String(index + 1).padStart(2, '0') }</small>
                    <h3>{ item.title }</h3>
                    <p>{ item.description }</p>
                  </ProcessItem>
                </Reveal>
              ))
            }
          </ProcessList>
        </Container>
      </ProcessBand>

      <Section4 id='team' focused={ false } />

      <Section id='pricing'>
        <Container>
          <Reveal>
            <SectionTitle>
              <Eyebrow>Budget Guide</Eyebrow>
              <h2>예산과 일정에 맞는 범위를 함께 정합니다.</h2>
              <p>
                정확한 견적은 기능 범위에 따라 달라집니다. 대신 가능한 범위와 어려운 범위를 먼저 나눕니다.
              </p>
            </SectionTitle>
          </Reveal>
          <PricingGrid>
            {
              pricingItems.map((item, index) => (
                <Reveal key={ item.title } delay={ index * 80 }>
                  <PricingCard>
                    <h3>{ item.title }</h3>
                    <strong>{ item.price }</strong>
                    <em>{ item.meta }</em>
                    <p>{ item.description }</p>
                  </PricingCard>
                </Reveal>
              ))
            }
          </PricingGrid>
        </Container>
      </Section>

      <Section id='faq'>
        <Container>
          <Reveal>
            <SectionTitle>
              <Eyebrow>FAQ</Eyebrow>
              <h2>문의 전에 궁금할 만한 것들</h2>
            </SectionTitle>
          </Reveal>
          <FaqGrid>
            {
              faqItems.map((item, index) => (
                <Reveal key={ item.question } delay={ index * 70 }>
                  <FaqCard>
                    <h3>{ item.question }</h3>
                    <p>{ item.answer }</p>
                  </FaqCard>
                </Reveal>
              ))
            }
          </FaqGrid>
        </Container>
      </Section>

      <ContactBand id='contact'>
        <Container>
          <ContactLayout>
            <Reveal>
              <ContactInfo>
                <SectionTitle>
                  <Eyebrow>Contact</Eyebrow>
                  <h2>아직 정리 중이어도 괜찮습니다.</h2>
                  <p>
                    현재 단계, 희망 일정, 예산 범위를 알려주시면 가능한 방향부터 같이 정리하겠습니다.
                  </p>
              </SectionTitle>
              <p>
                빠른 문의는 채널톡을 이용해 주세요. 문서로 정리해서 보내고 싶다면 <a href={ `mailto:${ CONTACT_EMAIL }` }>{ CONTACT_EMAIL }</a> 로 보내시면 됩니다.
              </p>
            </ContactInfo>
            </Reveal>

            <Reveal delay={ 120 }>
              <Form onSubmit={ onSubmit }>
                <Field>
                  이름 / 회사명
                  <input value={ form.name } onChange={ onInputChange('name') } placeholder='예: D405 HOUSE' required={ true } />
                </Field>
                <Field>
                  연락처
                  <input value={ form.contact } onChange={ onInputChange('contact') } placeholder='이메일 또는 전화번호' required={ true } />
                </Field>
                <Field>
                  의뢰 유형
                  <select value={ form.projectType } onChange={ onInputChange('projectType') }>
                    <option>MVP / PoC 개발</option>
                    <option>웹 / 앱 서비스 개발</option>
                    <option>업무 시스템 구축</option>
                    <option>운영 서비스 개선</option>
                    <option>아직 정하지 못함</option>
                  </select>
                </Field>
                <Field>
                  예산 범위
                  <select value={ form.budget } onChange={ onInputChange('budget') }>
                    <option>미정</option>
                    <option>1,000만원 이하</option>
                    <option>1,000만원 - 3,000만원</option>
                    <option>3,000만원 - 5,000만원</option>
                    <option>5,000만원 이상</option>
                  </select>
                </Field>
                <Field>
                  프로젝트 설명
                  <textarea value={ form.message } onChange={ onInputChange('message') } placeholder='만들고 싶은 서비스, 참고 링크, 희망 일정, 필요한 기능을 적어주세요.' required={ true } />
                </Field>
                <Button type='submit'>채널톡으로 상담 요청하기</Button>
              </Form>
            </Reveal>
          </ContactLayout>
        </Container>
      </ContactBand>

      <Footer>
        <Container>
          <strong>D405 HOUSE</strong>
          <p>대표: 문재영 | 사업자등록번호: 449-81-02181</p>
          <p>주소: 인천광역시 연수구 송도미래로11번길 27 1518호</p>
          <p>Email: { CONTACT_EMAIL }</p>
          <p>Copyright 2020. D405 HOUSE Corp. all rights reserved.</p>
        </Container>
      </Footer>
    </LandingPage>
  )
}

export default Landing
